import { IComboboxConfig } from "@dhx/ts-combobox";
import { ICalendarConfig } from "@dhx/ts-calendar";
import { ISliderConfig } from "@dhx/ts-slider";
import { IUploaderConfig } from "@dhx/ts-vault";
import { ITimepickerConfig } from "@dhx/ts-timepicker";
import { IEventSystem } from "@dhx/ts-common/events";
import { Layout } from "@dhx/ts-layout";
import { IAnyObj } from "@dhx/ts-common/types";
import { IColorpickerConfig } from "@dhx/ts-colorpicker";

export enum FormItemType {
	block = "block",
	input = "input",
	button = "button",
	combo = "combo",
	slider = "slider",
	radioButton = "radioButton",
	radioGroup = "radioGroup",
	checkbox = "checkbox",
	select = "select",
	simpleVault = "simpleVault",
	textarea = "textarea",
	timepicker = "timepicker",
	datepicker = "datepicker",
	colorpicker = "colorpicker",
	text = "text",
}

export type ValidationFn = (input: string) => boolean;

export interface IFormLayout {
	cellCss?: string;
	width?: string;
	height?: string;
	rows?: IFormItem[];
	cols?: IFormItem[];
	group?: FormItemType;
	groupName?: string;
	size?: number;
	title?: string;
	items?: IFormItem[];
	align?: "start" | "center" | "end" | "between" | "around" | "evenly";
	padding?: string;
	gravity?: boolean;
}

export interface IBlock extends IFormLayout {
	type?: FormItemType.block;
}
export interface IItem {
	type?: FormItemType;
	id?: string;
	parent?: string;
	width?: string;
	height?: string;
	css?: string;
	cellCss?: string;
	disabled?: boolean;
	labelWidth?: string;
	label?: string;
	hiddenLabel?: boolean;
	labelInline?: boolean;
	name?: string;
	required?: boolean;
	$validationStatus?: ValidationStatus;
	help?: string;
	preMessage?: string;
	successMessage?: string;
	errorMessage?: string;
	gravity?: boolean;
}

export interface IRadioGroup {
	type?: FormItemType.radioGroup;
	options: IRadioGroupOption;
	value?: string;
	id?: string;
	parent?: string;
	width?: string;
	height?: string;
	css?: string;
	disabled?: boolean;
	name?: string;
	required?: boolean;
	$validationStatus?: ValidationStatus;
	gravity?: boolean;
	preMessage?: string;
	successMessage?: string;
	errorMessage?: string;
	cellCss?: string;
	hidden?: boolean;
}

export interface IRadioGroupOption {
	cellCss?: string;
	width?: string;
	height?: string;
	rows?: IRadioButton[];
	cols?: IRadioButton[];
	align?: "start" | "center" | "end" | "between" | "around" | "evenly";
	padding?: string;
	gravity?: boolean;
}
export interface IRadioButton extends IItem {
	type?: FormItemType.radioButton;
	checked?: boolean;
	value?: string;
}

export interface IInput extends IItem {
	type?: FormItemType.input;
	validation?: Validation | ValidationFn;
	icon?: string;
	value?: string;
	placeholder?: string;
	inputType?: string;
	hidden?: boolean;
	autocomplete?: boolean;
}

export interface IText extends IItem {
	type?: FormItemType.text;
	value?: string;
}
export interface IButton extends IItem {
	type?: FormItemType.button;
	icon?: string;
	view?: "flat" | "link";
	size?: "small" | "medium";
	color?: "danger" | "secondary" | "primary" | "success";
	full?: boolean;
	circle?: boolean;
	loading?: boolean;
	value?: string;
	submit?: boolean;
}

export interface ICombo extends IItem, IComboboxConfig {
	type?: FormItemType.combo;
	validation?: ValidationFn;
	data?: any[];
	value?: string | string[];
}
export interface ISlider extends IItem, ISliderConfig {
	type?: FormItemType.slider;
}
export interface ICheckbox extends IItem {
	type?: FormItemType.checkbox;
	checked?: boolean;
	value?: string;
	hidden?: boolean;
}
export interface ITimeInput extends IItem, ITimepickerConfig {
	type?: FormItemType.timepicker;
	validation?: ValidationFn;
	icon?: string;
	value?: string;
	placeholder?: string;
	editing?: boolean;
}
export interface ICalendarInput extends IItem, ICalendarConfig {
	type?: FormItemType.datepicker;
	validation?: ValidationFn;
	icon?: string;
	value?: string;
	placeholder?: string;
	editing?: boolean;
}
export interface IColorpickerInput extends IItem, IColorpickerConfig {
	type?: FormItemType.colorpicker;
	validation?: ValidationFn;
	icon?: string;
	value?: string;
	placeholder?: string;
	editing?: boolean;
}
export interface IOption {
	value: string;
	content?: string;
	disabled?: boolean;
	selected?: boolean;
}
export interface ISelect extends IItem {
	validation?: ValidationFn;
	type?: FormItemType.select;
	options: IOption[];
	value?: string;
	icon?: string;
}
export interface ISimpleVault extends IItem, IUploaderConfig {
	type?: FormItemType.simpleVault;
	$vaultHeight?: number | string;
}
export interface ITextArea extends IItem {
	type?: FormItemType.textarea;
	validation?: Validation | ValidationFn;
	errorMessage?: string;
	value?: string;
	readOnly?: boolean;
	resizable?: boolean;
	placeholder?: string;
}
export interface ILabel {
	help?: string;
	id?: string;
	labelInline?: boolean;
	label?: string;
	labelWidth?: string;
}

export type IFormItemWithoutBlock = IInput | IButton | ICombo | ISlider | ICheckbox
	| IRadioButton | ICalendarInput | ISelect | ISimpleVault | ITextArea | ITimeInput | IText | IColorpickerInput | IRadioGroup;
export type IFormItem = IBlock | IFormItemWithoutBlock;

export enum FormEvents {
	change = "change",
	buttonClick = "buttonclick",
	validationFail = "validationfail",
	beforeSend = "beforesend",
	afterSend = "aftersend"
}

export interface IFormEventHandlersMap {
	[key: string]: (...args: any[]) => any;
	[FormEvents.change]: (name: string, value: any) => any;
	[FormEvents.buttonClick]: (id: string, e: MouseEvent) => any;
	[FormEvents.validationFail]: (id: string, component: any) => any;
	[FormEvents.beforeSend]: () => boolean;
	[FormEvents.afterSend]: () => void;
}

export enum BaseElementEvent {
	change = "change",
	configUpdate = "configUpdate"
}

export interface IBaseCheckboxEventHandlersMap {
	[key: string]: (...args: any[]) => any;
	[BaseElementEvent.change]: (arg: boolean) => void;
	[BaseElementEvent.configUpdate]: (config: ICheckbox) => void;
}

export interface IBaseColorpickerEventHandlersMap {
	[key: string]: (...args: any[]) => any;
	[BaseElementEvent.change]: (arg: string) => void;
	[BaseElementEvent.configUpdate]: (config: IColorpickerInput) => void;
}

export interface IBaseComboEventHandlersMap {
	[key: string]: (...args: any[]) => any;
	[BaseElementEvent.change]: (arg: string | string[]) => void;
	[BaseElementEvent.configUpdate]: (config: ICombo) => void;
}

export interface IBaseDatepickerEventHandlersMap {
	[key: string]: (...args: any[]) => any;
	[BaseElementEvent.change]: (arg: string) => void;
	[BaseElementEvent.configUpdate]: (config: ICalendarInput) => void;
}

export interface IBaseRadioGroupEventHandlersMap {
	[key: string]: (...args: any[]) => any;
	[BaseElementEvent.change]: () => void;
	[BaseElementEvent.configUpdate]: (config: IRadioGroup) => void;
}

export interface IBaseSelectEventHandlersMap {
	[key: string]: (...args: any[]) => any;
	[BaseElementEvent.change]: (arg: string) => void;
	[BaseElementEvent.configUpdate]: (config: ISelect) => void;
}

export interface IBaseSliderEventHandlersMap {
	[key: string]: (...args: any[]) => any;
	[BaseElementEvent.change]: (arg: string | number | number[]) => void;
	[BaseElementEvent.configUpdate]: (config: ISlider) => void;
}

export interface IBaseTimepickerEventHandlersMap {
	[key: string]: (...args: any[]) => any;
	[BaseElementEvent.change]: (arg: string) => void;
	[BaseElementEvent.configUpdate]: (config: ITimeInput) => void;
}

export interface IBaseSimpleVaultEventHandlersMap {
	[key: string]: (...args: any[]) => any;
	[BaseElementEvent.change]: (arg: string) => void;
	[BaseElementEvent.configUpdate]: (config: ITimeInput) => void;
}

export interface IBaseInputEventHandlersMap {
	[key: string]: (...args: any[]) => any;
	[BaseElementEvent.change]: (arg: string) => void;
	[BaseElementEvent.configUpdate]: (config: IInput | ITextArea | IText) => void;
}

export enum Validation {
	empty = "",
	validEmail = "email",
	validInteger = "integer",
	validNumeric = "numeric",
	validAplhaNumeric = "alphanumeric",
	validIPv4 = "IPv4",
}

export enum ValidationStatus {
	pre,
	error,
	success
}

export enum ClearMethod {
	value = "value",
	validation = "validation"
}

export interface IForm {
	config: IBlock;
	events: IEventSystem<FormEvents, IFormEventHandlersMap>;
	layout: Layout;

	setConfig(config: IBlock): void;
	send(url: string, method: string, asFormData?: boolean): Promise<any> | void;
	clear(method?: string): void;
	setValue(obj: FormData | IAnyObj): void;
	getValue(asFormData?: boolean): FormData | IAnyObj;
	getItem(id: string): IBlock;
	validate(): boolean;
	destructor(): void;
}